import { useMemo } from 'react';

import type { ContentfulAsset, ContentfulEntry } from '@order/Contentful';
import { useContentfulContentTypeEntry } from '@order/Contentful';

export const useUpgradeScreenLoadedContent = (
  tag?: SweetpassUpgradePageTag | '',
) => {
  const { data: contentfulData, fetching: isLoading } =
    useContentfulContentTypeEntry<ContentfulSweetpassUpgradePage>({
      contentType: SWEETPASS_UPGRADE_PAGE_CONTENT_ID,
      tags: tag ? [tag] : [],
      include: 2,
    });

  const content = useMemo(
    () =>
      contentfulData?.fields
        ? {
            content: contentfulData.fields.content.map(contentFromContentful),
            cta: ctaFromContentful(contentfulData.fields.cta),
          }
        : undefined,
    [contentfulData],
  );

  return {
    content,
    isLoading,
  };
};

// ––– TYPES –––––––––––––––––––––––––

export type SweetpassUpgradePageTag =
  | 'join-sweetpass'
  | 'upgrade-to-sweetpass-plus';

export type SweetpassUpgradeCtaHandler = (
  action: SweetpassUpgradeCta['action'],
) => void;

// ––– Upgrade Page –––––––––––––––––––––––––

export type SweetpassUpgradePage = {
  readonly content: readonly SweetpassUpgradeContent[];
  readonly cta: SweetpassUpgradeCta;
};

type ContentfulSweetpassUpgradePage = {
  readonly content: ReadonlyArray<
    ContentfulEntry<ContentfulSweetpassUpgradeContent>
  >;
  readonly cta: ContentfulEntry<ContentfulSweetpassUpgradeCta>;
};

// ––– Content (Header, Benefit List) ––––

export type SweetpassUpgradeContent =
  | SweetpassUpgradeHeader
  | SweetpassUpgradeBenefitList
  | SweetpassUpgradeUnknownContent;

type ContentfulSweetpassUpgradeContent =
  | ContentfulSweetpassUpgradeHeader
  | ContentfulSweetpassUpgradeBenefitList;

export type SweetpassUpgradeUnknownContent = {
  readonly type: string;
  readonly id: string;
};

const contentFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeContent>,
): SweetpassUpgradeContent => {
  const contentTypeId = entry.sys.contentType.sys.id;

  if (contentTypeId === 'sweetpassUpgradeHeader') {
    return headerFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassUpgradeHeader>,
    );
  }

  if (contentTypeId === 'sweetpassUpgradeBenefitList') {
    return benefitListFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassUpgradeBenefitList>,
    );
  }

  return {
    type: contentTypeId,
    id: entry.sys.id,
  };
};

// ––– Header –––––––––––––––––––––––––––––––––––

export type SweetpassUpgradeHeader = {
  readonly type: 'sweetpassUpgradeHeader';
  readonly id: string;
  readonly heroImage: {
    readonly url: string;
    readonly description: string;
  };
  readonly headingText: string;
  readonly bodyText: string;
  readonly cta: SweetpassUpgradeCta;
};

type ContentfulSweetpassUpgradeHeader = {
  readonly heroImage: ContentfulAsset;
  readonly headingTextV2: string;
  readonly bodyText: string;
  readonly cta: ContentfulEntry<ContentfulSweetpassUpgradeCta>;
};

const headerFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeHeader>,
): SweetpassUpgradeHeader => {
  return {
    type: 'sweetpassUpgradeHeader',
    id: entry.sys.id,
    heroImage: {
      url: entry.fields.heroImage.fields.file.url,
      description: entry.fields.heroImage.fields.description,
    },
    headingText: entry.fields.headingTextV2,
    bodyText: entry.fields.bodyText,
    cta: ctaFromContentful(entry.fields.cta),
  };
};

// ––– Benefit List ––––––––––––––––––––––––––––

export type SweetpassUpgradeBenefitList = {
  readonly type: 'sweetpassUpgradeBenefitList';
  readonly id: string;
  readonly benefits: readonly SweetpassUpgradeBenefit[];
};

type ContentfulSweetpassUpgradeBenefitList = {
  readonly benefitList: ReadonlyArray<
    ContentfulEntry<ContentfulSweetpassUpgradeBenefit>
  >;
};

const benefitListFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeBenefitList>,
): SweetpassUpgradeBenefitList => ({
  type: 'sweetpassUpgradeBenefitList',
  id: entry.sys.id,
  benefits: entry.fields.benefitList.map(benefitFromContentful),
});

// ––– Benefit ––––––––––––––––––––––––––––––––––

export type SweetpassUpgradeBenefit = {
  readonly id: string;
  readonly image: {
    readonly url: string;
    readonly description: string;
  };
  readonly titleText: string;
  readonly bodyText: string;
};

type ContentfulSweetpassUpgradeBenefit = {
  readonly image: ContentfulAsset;
  readonly titleText: string;
  readonly bodyText: string;
};

const benefitFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeBenefit>,
): SweetpassUpgradeBenefit => ({
  id: entry.sys.id,
  image: {
    description: entry.fields.image.fields.description,
    url: entry.fields.image.fields.file.url,
  },
  titleText: entry.fields.titleText,
  bodyText: entry.fields.bodyText,
});

// ––– CTA ––––––––––––––––––––––––––––

export type SweetpassUpgradeCtaAction =
  | 'join_sweetpass'
  | 'upgrade_to_sweetpass_plus';

export type SweetpassUpgradeCta = {
  readonly title: string;
  readonly action: SweetpassUpgradeCtaAction;
};

type ContentfulSweetpassUpgradeCta = {
  readonly title: string;
  readonly action: string;
};

const ctaFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeCta>,
): SweetpassUpgradeCta => ({
  action: entry.fields.action as SweetpassUpgradeCta['action'],
  title: entry.fields.title.replace('{cost}', '$10/month'),
});

const SWEETPASS_UPGRADE_PAGE_CONTENT_ID = 'sweetpassUpgradePage';
